import { createSlice } from "@reduxjs/toolkit";
import {
  getContractVersionsThunk,
  getContractVersionByIdThunk,
  createContractVersionThunk,
  updateContractVersionThunk,
  deleteContractVersionThunk,
  submitContractVersionThunk,
  approveContractVersionThunk,
  rejectContractVersionThunk,
  applyContractVersionThunk,
  getActiveContractStateThunk,
} from "state/action/contract_version";

const asyncState = (data = null) => ({ data, isFulfilled: false, isPending: false, isRejected: false, err: null });
const pending = (s) => ({ ...s, isPending: true, isFulfilled: false, isRejected: false, err: null });
const fulfilled = (s, data) => ({ ...s, isPending: false, isFulfilled: true, data });
const rejected = (s, err) => ({ ...s, isPending: false, isRejected: true, err });

const initialState = {
  getVersions: asyncState(),
  getVersionById: asyncState(),
  createVersion: asyncState(),
  updateVersion: asyncState(),
  deleteVersion: asyncState(),
  submitVersion: asyncState(),
  approveVersion: asyncState(),
  rejectVersion: asyncState(),
  applyVersion: asyncState(),
  getActiveState: asyncState(),
  selectedVersion: null,
};

export const contractVersionSlice = createSlice({
  name: "contractVersion",
  initialState,
  reducers: {
    setSelectedVersion: (state, action) => { state.selectedVersion = action.payload; },
    clearSelectedVersion: (state) => { state.selectedVersion = null; },
    clearWorkflow: (state) => {
      state.submitVersion = asyncState();
      state.approveVersion = asyncState();
      state.rejectVersion = asyncState();
      state.applyVersion = asyncState();
    },
  },
  extraReducers(builder) {
    const cases = [
      [getContractVersionsThunk, "getVersions"],
      [getContractVersionByIdThunk, "getVersionById"],
      [createContractVersionThunk, "createVersion"],
      [updateContractVersionThunk, "updateVersion"],
      [deleteContractVersionThunk, "deleteVersion"],
      [submitContractVersionThunk, "submitVersion"],
      [approveContractVersionThunk, "approveVersion"],
      [rejectContractVersionThunk, "rejectVersion"],
      [applyContractVersionThunk, "applyVersion"],
      [getActiveContractStateThunk, "getActiveState"],
    ];
    for (const [thunk, key] of cases) {
      builder
        .addCase(thunk.pending, (state) => { state[key] = pending(state[key]); })
        .addCase(thunk.fulfilled, (state, action) => {
          state[key] = fulfilled(state[key], action.payload);
          if (key === "getVersionById") state.selectedVersion = action.payload?.data ?? action.payload;
        })
        .addCase(thunk.rejected, (state, action) => { state[key] = rejected(state[key], action.error.message); });
    }
  },
});

export const contractVersionAction = {
  ...contractVersionSlice.actions,
  getContractVersionsThunk,
  getContractVersionByIdThunk,
  createContractVersionThunk,
  updateContractVersionThunk,
  deleteContractVersionThunk,
  submitContractVersionThunk,
  approveContractVersionThunk,
  rejectContractVersionThunk,
  applyContractVersionThunk,
  getActiveContractStateThunk,
};

export const selectContractVersion = (state) => state.contractVersion;
export const selectVersions = (state) => state.contractVersion?.getVersions?.data?.data ?? [];
export const selectSelectedVersion = (state) => state.contractVersion?.selectedVersion;

export default contractVersionSlice.reducer;
